import { AccountData, NewAccountFormData, CreateNoteData, Note, AssignedModule } from '../types';
import config from "../../../config";
import { HttpRequest } from "../../../utils";
import { useParams } from 'react-router';

const request = new HttpRequest(config.endpoints.accounts);
const userRequest = new HttpRequest(config.endpoints.users);
const requestModules = new HttpRequest(config.endpoints.modules);

export const requestLogin = async (login: any) => {
  const response = await request.post(login, 'login');

  if (response.status == 200) {
    return response.data.data;
  }

  return null;
}
export const accountService = {
  async findAccounts(): Promise<AccountData[]> {
    try {
      const response = await request.get();
      const accountsData = response.data.accountsData || [];

      if (Array.isArray(accountsData)) {
        return accountsData;
      }

      return [];
    } catch (error) {
      return [];
    }
  },



  async findAccountById(id: string): Promise<AccountData | null> {
    try {
      const response = await request.get(id);
      return response.data.accountData;
    } catch (error) {
      return null;
    }
  },


  async updateAccountStatus(accountId: string, status: string): Promise<void> {
    try {
      const response = await request.put(accountId, { status });
    } catch (error) {
      throw new Error(`Failed to update status for account ID ${accountId}`);
    }
  },

  async updateAccount(accountId: string, payload: any): Promise<any> {
    try {
      const response = await request.put(accountId, payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async createAccount(accountData: NewAccountFormData): Promise<AccountData> {
    try {
      const response = await request.post(accountData);
      return response.data.accountData;
    } catch (error) {
      throw new Error('Failed to create account. Please try again.');
    }
  },

  findAdminUser: async (accountId: string) => {
    try {
      const response = await request.get(`${accountId}/admin`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getAccounts: async () => {
    try {
      const response = await request.get();
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async updateAccountUserData(accountId: string, userData: Partial<{
    email: string;
    name: string;
    phoneNumber: string;
    country: string;
    companyName: string;
  }>): Promise<void> {
    try {
      // Filter out undefined values and exclude country
      const { country, ...editedData } = userData;

      // Ensure email is first in the object
      const requestData = {
        email: userData.email,
        ...editedData
      };

      const response = await userRequest.put(`${accountId}`, requestData);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to update user data for account ID ${accountId}`);
    }
  },

  async createAccountNote(accountId: string, noteData: CreateNoteData): Promise<Note[]> {
    try {
      const response = await request.post(noteData, `${accountId}/notes`);

      // El backend puede devolver el array directamente o dentro de una propiedad
      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data && Array.isArray(response.data.notes)) {
        return response.data.notes;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        return response.data.data;
      }

      // Si no se puede determinar, hacer un GET para obtener las notas actualizadas
      return await this.getAccountNotes(accountId);
    } catch (error) {
      throw new Error(`Failed to create note for account ID ${accountId}`);
    }
  },

  async getAccountNotes(accountId: string): Promise<Note[]> {
    try {
      const response = await request.get(accountId);

      if (response.data && response.data.accountData && response.data.accountData.notes) {
        return response.data.accountData.notes;
      }

      return [];
    } catch (error) {
      throw new Error(`Failed to fetch account data for ID ${accountId}`);
    }
  },

  async deleteAccountNote(accountId: string, noteId: string): Promise<void> {
    try {
      const response = await request.delete(`${accountId}/notes/${noteId}`);

      // Fetch updated notes after deletion
      const updatedNotes = await this.getAccountNotes(accountId);
      return updatedNotes;
    } catch (error) {
      throw new Error(`Failed to delete note for account ID ${accountId}`);
    }
  },

  async updateAccountNote(accountId: string, noteId: string, noteData: CreateNoteData): Promise<Note[]> {
    try {
      const response = await request.put(`${accountId}/notes/${noteId}`, noteData);

      // Fetch updated notes after update
      const updatedNotes = await this.getAccountNotes(accountId);
      return updatedNotes;
    } catch (error) {
      throw new Error(`Failed to update note for account ID ${accountId}`);
    }
  },
  getAccountModules: async (accountId: string) => {
    try {
      const response = await requestModules.get(`accounts/${accountId}`);

      // El backend devuelve { message, modulesData }
      if (response.data && response.data.modulesData) {
        return response.data.modulesData;
      }

      // Fallback si el formato es diferente
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateAccountModule: async (moduleId: string, accountId: string, isEnabled: boolean, userId: string) => {
    try {

      const response = await requestModules.put(`${moduleId}/accounts/${accountId}/status`, { isEnabled, modifiedBy: userId });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getAllModules: async () => {
    try {
      const response = await requestModules.get();
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  assignModules: async (accountId: string, modules: AssignedModule) => {
    try {
      const response = await requestModules.post(modules, `accounts/${accountId}/assign`);
      return response.data;

    } catch (error) {
      throw error;
    }
  }
}
